import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    inviteTeamMember,
    acceptInvitation,
    resendInvitation,
    getTeamMembers,
    removeTeamMember,
    updateMemberRole,
    assignMemberToSubteam
} from 'team/apis/teamMemberApi';
import { useAuth } from 'contexts/AuthContext';
import { useError } from 'contexts/ErrorContext';

const TeamMemberContext = createContext();

export const TeamMemberProvider = ({ children }) => {
    const { getCurrentUserId, isAuthenticated } = useAuth();
    const { addError } = useError();

    // Team member state management
    const [teamMembers, setTeamMembers] = useState({}); // { teamId: { members: [], pagination: {} } }
    const [memberError, setMemberError] = useState(null);
    const [loading, setLoading] = useState(false);

    // Cache management
    const [memberCache, setMemberCache] = useState(new Map());

    // Helper function to check if user is authenticated
    const requireAuth = useCallback(() => {
        if (!isAuthenticated || !getCurrentUserId()) {
            throw new Error('Authentication required');
        }
        return getCurrentUserId();
    }, [isAuthenticated, getCurrentUserId]);

    // Enhanced error handling for member operations
    const handleMemberError = useCallback((error, operation = 'Member operation') => {
        console.error(`[TeamMemberContext] ${operation} error:`, error);
        const errorMessage = error.message || 'An unexpected error occurred';
        setMemberError(errorMessage);
        addError(errorMessage);
    }, [addError]);

    // Clear member error
    const clearMemberError = useCallback(() => {
        setMemberError(null);
    }, []);

    // Cache key generator
    const getCacheKey = useCallback((teamId, operation = 'default') => {
        return `${teamId}-${operation}`;
    }, []);

    // Invite team member
    const inviteMember = useCallback(async (teamId, memberData) => {
        try {
            requireAuth();
            setLoading(true);
            clearMemberError();

            const result = await inviteTeamMember(teamId, memberData);

            // Invalidate cache
            const cacheKey = getCacheKey(teamId, 'members');
            memberCache.delete(cacheKey);

            console.log('[TeamMemberContext] Member invited successfully:', result);
            return { success: true, data: result };
        } catch (error) {
            handleMemberError(error, 'Inviting team member');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [requireAuth, clearMemberError, handleMemberError, getCacheKey, memberCache]);

    // Accept team invitation
    const acceptTeamInvitation = useCallback(async (invitationToken) => {
        try {
            requireAuth();
            setLoading(true);
            clearMemberError();

            const result = await acceptInvitation(invitationToken);

            // Clear all caches
            setMemberCache(new Map());

            console.log('[TeamMemberContext] Invitation accepted successfully:', result);
            return { success: true, data: result };
        } catch (error) {
            handleMemberError(error, 'Accepting team invitation');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [requireAuth, clearMemberError, handleMemberError]);

    // Resend invitation
    const resendTeamInvitation = useCallback(async (teamId, userId) => {
        try {
            requireAuth();
            setLoading(true);
            clearMemberError();

            const result = await resendInvitation(teamId, userId);

            console.log('[TeamMemberContext] Invitation resent successfully');
            return { success: true, data: result };
        } catch (error) {
            handleMemberError(error, 'Resending invitation');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [requireAuth, clearMemberError, handleMemberError]);

    // Get team members
    const getMembers = useCallback(async (teamId, options = {}) => {
        try {
            requireAuth();
            setLoading(true);
            clearMemberError();

            const result = await getTeamMembers(teamId, options);

            // Update state
            // Handle both { members: [], pagination: {} } and direct array formats
            const membersData = result?.members || result || [];
            setTeamMembers(prev => ({
                ...prev,
                [teamId]: {
                    members: Array.isArray(membersData) ? membersData : [],
                    pagination: result?.pagination || {}
                }
            }));
            
            console.log('[TeamMemberContext] Updated teamMembers state:', {
                teamId,
                membersCount: Array.isArray(membersData) ? membersData.length : 0,
                members: Array.isArray(membersData) ? membersData.map(m => ({
                    _id: m._id,
                    userId: m.userId,
                    hasUser: !!m.user,
                    hasDefaultCard: !!m.defaultCard
                })) : []
            });

            // Cache the result
            const cacheKey = getCacheKey(teamId, 'members');
            setMemberCache(prev => new Map(prev.set(cacheKey, {
                data: result,
                timestamp: Date.now()
            })));

            console.log('[TeamMemberContext] Team members fetched successfully:', result);
            return { success: true, data: result };
        } catch (error) {
            handleMemberError(error, 'Fetching team members');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [requireAuth, clearMemberError, handleMemberError, getCacheKey]);

    // Remove team member
    const removeMember = useCallback(async (teamId, userId) => {
        try {
            requireAuth();
            setLoading(true);
            clearMemberError();

            const result = await removeTeamMember(teamId, userId);

            // Update state
            setTeamMembers(prev => {
                const teamData = prev[teamId];
                if (teamData && teamData.members) {
                    return {
                        ...prev,
                        [teamId]: {
                            ...teamData,
                            members: teamData.members.filter(member => member.userId !== userId)
                        }
                    };
                }
                return prev;
            });

            // Invalidate cache
            const cacheKey = getCacheKey(teamId, 'members');
            memberCache.delete(cacheKey);

            console.log('[TeamMemberContext] Member removed successfully');
            return { success: true };
        } catch (error) {
            handleMemberError(error, 'Removing team member');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [requireAuth, clearMemberError, handleMemberError, getCacheKey, memberCache]);

    // Update member role
    const updateRole = useCallback(async (teamId, userId, newRole) => {
        try {
            requireAuth();
            setLoading(true);
            clearMemberError();

            const result = await updateMemberRole(teamId, userId, newRole);

            // Update state
            setTeamMembers(prev => {
                const teamData = prev[teamId];
                if (teamData && teamData.members) {
                    return {
                        ...prev,
                        [teamId]: {
                            ...teamData,
                            members: teamData.members.map(member =>
                                member.userId === userId
                                    ? { ...member, role: newRole }
                                    : member
                            )
                        }
                    };
                }
                return prev;
            });

            // Invalidate cache
            const cacheKey = getCacheKey(teamId, 'members');
            memberCache.delete(cacheKey);

            console.log('[TeamMemberContext] Member role updated successfully');
            return { success: true, data: result };
        } catch (error) {
            handleMemberError(error, 'Updating member role');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [requireAuth, clearMemberError, handleMemberError, getCacheKey, memberCache]);

    // Assign member to subteam
    const assignToSubteam = useCallback(async (teamId, userId, subteamId, isSubteamAdmin = false) => {
        try {
            requireAuth();
            setLoading(true);
            clearMemberError();

            const result = await assignMemberToSubteam(teamId, userId, subteamId, isSubteamAdmin);

            // Invalidate cache
            const cacheKey = getCacheKey(teamId, 'members');
            memberCache.delete(cacheKey);

            console.log('[TeamMemberContext] Member assigned to subteam successfully');
            return { success: true, data: result };
        } catch (error) {
            handleMemberError(error, 'Assigning member to subteam');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [requireAuth, clearMemberError, handleMemberError, getCacheKey, memberCache]);

    // Get member by ID from current state
    const getMemberById = useCallback((teamId, userId) => {
        const teamData = teamMembers[teamId];
        if (!teamData || !teamData.members) return null;
        return teamData.members.find(member => member.userId === userId);
    }, [teamMembers]);

    // Refresh team members
    const refreshTeamMembers = useCallback(async (teamId) => {
        try {
            setLoading(true);
            clearMemberError();

            // Clear cache for this team
            const cacheKey = getCacheKey(teamId, 'members');
            memberCache.delete(cacheKey);

            // Fetch fresh data
            await getMembers(teamId);

            console.log('[TeamMemberContext] Team members refreshed successfully');
        } catch (error) {
            handleMemberError(error, 'Refreshing team members');
        } finally {
            setLoading(false);
        }
    }, [getMembers, clearMemberError, handleMemberError, getCacheKey, memberCache]);

    // Clear all member data
    const clearAllMemberData = useCallback(() => {
        setTeamMembers({});
        setMemberCache(new Map());
        clearMemberError();
        console.log('[TeamMemberContext] All member data cleared');
    }, [clearMemberError]);

    // Context value
    const value = {
        // State
        teamMembers,
        memberError,
        loading,

        // Actions
        inviteMember,
        acceptTeamInvitation,
        resendTeamInvitation,
        getMembers,
        removeMember,
        updateRole,
        assignToSubteam,

        // Utility functions
        getMemberById,
        refreshTeamMembers,
        clearAllMemberData,
        clearMemberError
    };

    return (
        <TeamMemberContext.Provider value={value}>
            {children}
        </TeamMemberContext.Provider>
    );
};

export const useTeamMember = () => {
    const context = useContext(TeamMemberContext);
    if (!context) {
        throw new Error('useTeamMember must be used within a TeamMemberProvider');
    }
    return context;
};